/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Alert } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import HeaderActionButton from 'src/components/ActionButton/HeaderActionButton'
import HeaderBackIcon from './HeaderBackIcon'
import HeaderCancelIcon from './HeaderCancelIcon'
import HeaderSaveIcon from './HeaderSaveIcon'
import Text from 'src/components/ts-components/Text'
import { isCanlan } from '@sportninja/common/utils/customer-name'

type HeaderProps = {
  onBack: () => void
  onCancel: () => void
  onSubmit: () => void
  isLoading: boolean
  submitError: string | null
  competitionName?: string
}

const headerContainerStyle = css`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding-bottom: 24px;
  border-bottom: 1px solid ${colors.SOFT_STEEL};
  margin-bottom: 24px;
`

const leftSideStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`

const backLinkStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const rightSideStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
`

const Header = ({
  onBack,
  onCancel,
  onSubmit,
  isLoading,
  submitError,
  competitionName,
}: HeaderProps) => {
  return (
    <div>
      <div css={headerContainerStyle}>
        <div css={leftSideStyle}>
          <button type='button' css={backLinkStyle} onClick={onBack}>
            <HeaderBackIcon />
            <Text
              variant='captionXSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
              style='margin: 0; letter-spacing: 1.44px;'
            >
              BACK
            </Text>
          </button>
          <Text
            variant='displaySmall'
            weight='semiBold'
            color={colors.WHITE}
            style={`${
              isCanlan
                ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
                : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'
            }`}
          >
            Registration Setup
          </Text>
          {competitionName ? (
            <Text
              variant='paragraphSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {competitionName}
            </Text>
          ) : null}
        </div>
        <div css={rightSideStyle}>
          <HeaderActionButton
            text='Cancel'
            onClick={onCancel}
            variant='secondary'
            textWeight='medium'
            disabled={isLoading}
            icon={<HeaderCancelIcon />}
          />
          <HeaderActionButton
            text='Save'
            onClick={onSubmit}
            variant='primary'
            textWeight='bold'
            disabled={isLoading}
            isLoading={isLoading}
            icon={<HeaderSaveIcon />}
          />
        </div>
      </div>
      {submitError && (
        <Alert
          type='error'
          showIcon
          message={submitError}
          css={css`
            margin-bottom: 16px;
          `}
        />
      )}
    </div>
  )
}

export default Header
